import { AlpineComponent } from "alpinejs";

export interface SingleSelectOption {
    label: string;
    items: {
        label: string;
        identifier: string;
    }[];
}

export interface SingleGroupOption {
    group: {
        label: string;
        identifier: string;
    }[];
}

export interface ToggleOption {
    label: string;
    identifier: string;
}

export interface Filters {
    [key: string]: (SingleSelectOption | SingleGroupOption | ToggleOption)[];
}

export interface FilterComponent {
    filters?: Filters;
    multiple: boolean;
    filterContainer(): HTMLElement;
    serverRequest: boolean;
    singleSelection: string | null;
    objectSelection: { [key: string]: string | null };
    refresh: number;
    filterUrl: URL;
    init(): void;
    inBackend(): boolean;
    initFilter(hasFiltersCallback?: () => void): void;
    decodeBrackets(part: string): string;
    changeKey(key: string, value: string): void;
    removeKey(key: string): void;
    resetKeys(): void;
    selection(): string | { [key: string]: string | null };

    /**
     * Toggle the option with the given key. It will be set to true or false.
     */
    toggleOption(key: string, applyUrl?: boolean): void;

    /**
     * Toggle the option with the given key.
     * It will be set to the given identifier or set to null if the value is already set and the same.
     */
    toggleGroupOption(key: string, identifier: string, applyUrl?: boolean): void;

    /**
     * Select from a dropdown. Only one item can be selected.
     * If an item has identifier null, then it will match all items.
     */
    selectDropdownOption(key: string, identifier: string, applyUrl?: boolean): void;

    /**
     * The item is visible if the mode is multiple and all selected options are equal to the item's data attributes.
     * If the mode is single, the item is visible if the item's data-tags contain the selected option.
     */
    isFilterItemVisible(element?: HTMLElement): boolean;
    clearFilter(): void;
    visibleResults(): number;
}

/**
 * @param filters The filters object provided to the backend (only for debugging)
 * @param filterContainer The container of the filter items
 * @param multiple If true, multiple options can be selected. If false, only one option can be selected.
 */
export default function filter(
    filters?: Filters,
    multiple = false,
    filterContainer = "filterItemContainer"
): AlpineComponent<FilterComponent> {
    return {
        filters,
        multiple,
        filterContainer() {
            return this.$refs[filterContainer];
        },
        serverRequest: false,
        singleSelection: null,
        objectSelection: {},
        refresh: 0,
        filterUrl: new URL(window.location.toString()),
        init() {
            this.initFilter();
        },
        inBackend() {
            return document.body.classList.contains("neos-backend");
        },
        initFilter(hasFiltersCallback?: () => void) {
            if (this.inBackend()) {
                return;
            }
            const fullKeys = this.filterUrl.searchParams.keys();
            for (const fullKey of fullKeys) {
                if (!fullKey.match(/^(filter\[.*\])$/)) {
                    continue;
                }

                if (this.filterUrl.searchParams.get(fullKey) === "") {
                    const regex = /filter\[([a-zA-Z0-9\-_]+)\]/gm;

                    let m;
                    while ((m = regex.exec(fullKey)) !== null) {
                        if (m.index === regex.lastIndex) {
                            regex.lastIndex++;
                        }

                        this.toggleOption(m[1], false);
                    }
                } else {
                    const values = this.filterUrl.searchParams.getAll(fullKey);
                    if (values.length === 1) {
                        const regex = /filter\[([a-zA-Z0-9\-_]+)\]/gm;

                        let m;
                        while ((m = regex.exec(fullKey)) !== null) {
                            if (m.index === regex.lastIndex) {
                                regex.lastIndex++;
                            }

                            this.selectDropdownOption(m[1], values[0], false);
                        }
                    }
                    // multiple values are not supported
                }
            }
            if (fullKeys.length > 0) {
                hasFiltersCallback?.();
            }
        },
        decodeBrackets(part: string) {
            return decodeURIComponent(part.replace(/%5B/g, "[").replace(/%5D/g, "]"));
        },
        changeKey(key, value) {
            this.filterUrl.searchParams.set(`filter[${key}]`, value);
            window.history.pushState({}, "", this.decodeBrackets(this.filterUrl.toString()));
        },
        removeKey(key) {
            this.filterUrl.searchParams.delete(`filter[${key}]`);
            window.history.pushState({}, "", this.decodeBrackets(this.filterUrl.toString()));
        },
        resetKeys() {
            const fullKeys = [...this.filterUrl.searchParams.keys()];
            for (const fullKey of fullKeys) {
                this.filterUrl.searchParams.delete(fullKey);
            }
            window.history.pushState({}, "", this.decodeBrackets(this.filterUrl.toString()));
        },
        selection() {
            return this.multiple ? this.objectSelection.tags : this.singleSelection;
        },
        toggleOption(key: string, applyUrl = true) {
            if (this.multiple) {
                this.objectSelection[key] = Boolean(!this.objectSelection[key]);
                if (applyUrl && !this.inBackend()) {
                    if (this.objectSelection[key]) {
                        this.removeKey(key);
                    } else {
                        this.changeKey(key, "");
                    }
                }
            } else {
                if (this.singleSelection === key) {
                    this.singleSelection = null;
                    applyUrl && !this.inBackend() && this.removeKey(key);
                } else {
                    this.singleSelection = key;
                    applyUrl && !this.inBackend() && this.changeKey(key, "");
                }
            }
            this.$dispatch("filterchange");
        },
        toggleGroupOption(key: string, identifier, applyUrl = true) {
            if (this.multiple) {
                if (this.objectSelection[key] === identifier) {
                    this.objectSelection[key] = null;
                    applyUrl && !this.inBackend() && this.removeKey(key);
                } else {
                    this.objectSelection[key] = identifier;
                    applyUrl && !this.inBackend() && this.changeKey(key, identifier);
                }
            } else {
                if (this.singleSelection === key) {
                    this.singleSelection = null;
                    applyUrl && !this.inBackend() && this.removeKey(key);
                } else {
                    this.singleSelection = key;
                    applyUrl && !this.inBackend() && this.changeKey(key, "");
                }
            }
            this.$dispatch("filterchange");
        },
        selectDropdownOption(key: string, identifier, applyUrl = true) {
            if (this.multiple) {
                this.objectSelection[key] = identifier;
                if (applyUrl && !this.inBackend()) {
                    if (identifier === null) {
                        this.removeKey(key);
                    } else {
                        this.changeKey(key, identifier);
                    }
                }
            } else {
                if (identifier === null) {
                    this.singleSelection = null;
                    applyUrl && !this.inBackend() && this.removeKey(key);
                } else {
                    this.singleSelection = key;
                    applyUrl && !this.inBackend() && this.changeKey(key);
                }
            }
            this.$dispatch("filterchange");
        },
        isFilterItemVisible(element) {
            const el = element ?? this.$el;
            if (this.multiple) {
                for (const key in this.objectSelection) {
                    if (
                        this.objectSelection[key] != null &&
                        el.dataset[key] &&
                        (el.dataset[key].includes("[")
                            ? !el.dataset[key].includes(this.objectSelection[key])
                            : this.objectSelection[key] !== el.dataset[key])
                    ) {
                        return false;
                    }
                }
                return true;
            } else {
                return (
                    this.singleSelection == null ||
                    (el.dataset?.tags && JSON.parse(el.dataset.tags).includes(this.singleSelection))
                );
            }
        },
        clearFilter() {
            this.singleSelection = null;
            this.objectSelection = {};
            this.resetKeys();
        },
        visibleResults() {
            if (typeof this.filterContainer() == "undefined") {
                console.error("filterContainer is undefined. Please define one in your template.");
                return 0;
            }
            let result = 0;
            this.refresh++;
            for (const el of this.filterContainer().children) {
                if (this.isFilterItemVisible(el)) {
                    result++;
                }
            }
            return result;
        },
    };
}
